import { z } from "zod";

import type { ContractDocument } from "./contractContent";

/**
 * ŞABLON DEĞİŞKENLERİ (CLOS-132 `[113]`).
 *
 * ### ⚠ Sözdizimi DAR
 *
 * `{{ ad }}` — nokta ve alt çizgi dışında bir şey yok. Zengin bir ifade dili (koşul,
 * döngü, filtre) eklemek, şablonun içine bir program gömmek olurdu: hukukçunun yazdığı
 * metin bir daha gözle doğrulanamaz hâle gelir ve her yeni sözdizimi yeni bir hata yüzeyi
 * açardı.
 *
 * ### ⚠ Bilinmeyen değişken SESSİZCE KAYBOLMAZ
 *
 * Kartın doğrudan kriteri. Doldurulmamış bir yer boş bırakılsaydı, sözleşme metninde
 * fark edilmeyen bir boşluk kalırdı — hukuki bir belgede en tehlikeli hata türü. Bu yüzden
 * eksik değişkenler HATA, fazladan gönderilenler de HATA.
 */

/** Metindeki değişken işareti: `{{ musteri_adi }}`. */
export const TEMPLATE_VARIABLE_PATTERN = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/**
 * SİSTEMİN doldurduğu değişkenler — kullanıcıdan istenmiyor.
 *
 * ⚠ Küme KAPALI ve küçük: her sistem değişkeni, kullanıcının göremediği bir yerden gelen
 * bir metin demek. "Yeni sözleşme bilgileriyle doldur" kartın kapsamı; başlık ve tarih
 * o bilginin kendisi.
 */
export const TEMPLATE_SYSTEM_VARIABLES = {
  "sozlesme.baslik": "Sözleşme başlığı",
  "sistem.bugun": "Bugünün tarihi",
  "firma.ad": "Çalışma alanı adı",
} as const;
export type TemplateSystemVariable = keyof typeof TEMPLATE_SYSTEM_VARIABLES;

export function isSystemVariable(ad: string): ad is TemplateSystemVariable {
  return Object.hasOwn(TEMPLATE_SYSTEM_VARIABLES, ad);
}

/** Şablon metnindeki değişkenler — sırayla, tekrarsız. */
export function extractTemplateVariables(document: ContractDocument | null): string[] {
  if (!document) return [];
  const bulunan: string[] = [];
  for (const madde of document.clauses) {
    for (const metin of [madde.title ?? "", ...madde.blocks.map((b) => b.text)]) {
      for (const eslesme of metin.matchAll(TEMPLATE_VARIABLE_PATTERN)) {
        const ad = eslesme[1]!;
        if (!bulunan.includes(ad)) bulunan.push(ad);
      }
    }
  }
  return bulunan;
}

export type TemplateVariableView = {
  name: string;
  /** Sistem dolduruyorsa kullanıcıdan istenmiyor. */
  filledBySystem: boolean;
  label: string;
};

/**
 * DEĞİŞKEN ADINDAN EKRAN ETİKETİ.
 *
 * ⚠ HAM AD KULLANICIYA GÖSTERİLMEZ — saha bulgusu (4 Eylül 2026): "Şablondan sözleşme"
 * ekranında alan etiketi olarak `musteri_adi` yazıyordu. Bu, sistemin iç adının ürün
 * yüzeyine sızması; aynı raporun "kullanıcıdan ham organizasyon kimliği istenmesi"
 * bulgusuyla aynı sınıf. Hukukçu `musteri_adi` diye bir alan doldurmuyor, müşteri adını
 * yazıyor.
 *
 * ⚠ ETİKET TÜRETİLİYOR, UYDURULMUYOR. Alt çizgi ve nokta boşluğa dönüyor, ilk harf
 * büyüyor — o kadar. Daha fazlası yapılamaz: değişken sözdizimi ASCII (`[a-zA-Z0-9_.]`),
 * yani `musteri_adi`'nden "Müşteri adı" üretmek eksik harfleri TAHMİN etmek olurdu.
 * Şablonu yazan hukukçunun metnine sistemin tahmin eklemesi, bir sözleşmede en
 * istenmeyecek şey. Şablon yazarı düzgün Türkçe etiket istiyorsa değişkeni öyle
 * adlandırıyor; sistem onun yerine karar vermiyor.
 *
 * ⚠ HAM AD KAYBOLMUYOR: `name` alanında duruyor ve sunucunun alan hataları onu kullanıyor.
 */
export function humanizeTemplateVariable(ad: string): string {
  const bosluklu = ad.replaceAll(/[._]+/g, " ").trim();
  if (bosluklu.length === 0) return ad;
  return bosluklu[0]!.toLocaleUpperCase("tr-TR") + bosluklu.slice(1);
}

export function describeTemplateVariables(
  document: ContractDocument | null,
): TemplateVariableView[] {
  return extractTemplateVariables(document).map((ad) => ({
    name: ad,
    filledBySystem: isSystemVariable(ad),
    label: isSystemVariable(ad) ? TEMPLATE_SYSTEM_VARIABLES[ad] : humanizeTemplateVariable(ad),
  }));
}

export type TemplateFillResult = {
  document: ContractDocument;
  /** Metinde var ama değeri verilmemiş değişkenler. */
  missing: string[];
  /** Değer verilmiş ama metinde OLMAYAN değişkenler. */
  unknown: string[];
};

/**
 * Değişkenleri doldurur.
 *
 * ⚠ Kaynak belgeye DOKUNMUYOR: yeni bir belge üretiliyor. Kart *"kaynak şablon
 * DEĞİŞMEZ"* diyor ve yerinde değiştirseydik, bir kez doldurulan kalıp kütüphanede
 * doldurulmuş hâliyle kalırdı.
 *
 * ⚠ Eksik ve fazla değişkenler DÖNDÜRÜLÜYOR, atılmıyor: karar çağıranın (kartın
 * *"bilinmeyen değişken sessizce kaybolmaz"* kuralı bu iki listeyle uygulanıyor).
 */
export function fillTemplateVariables(
  document: ContractDocument,
  values: Record<string, string>,
): TemplateFillResult {
  const degiskenler = extractTemplateVariables(document);
  const missing = degiskenler.filter((ad) => !(ad in values));
  const unknown = Object.keys(values).filter((ad) => !degiskenler.includes(ad));

  const doldur = (metin: string) =>
    metin.replace(TEMPLATE_VARIABLE_PATTERN, (tam, ad: string) => values[ad] ?? tam);

  return {
    document: {
      clauses: document.clauses.map((madde) => ({
        ...madde,
        title: doldur(madde.title),
        blocks: madde.blocks.map((blok) => ({ ...blok, text: doldur(blok.text) })),
      })),
    },
    missing,
    unknown,
  };
}

/**
 * Değişken değerleri.
 *
 * ⚠ Yalnız DÜZ METİN: değer olarak yapı kabul etseydik şablon bir veri şablonuna dönüşür
 * ve metnin içine ne konduğu gözle doğrulanamazdı.
 */
export const templateVariableValuesSchema = z.record(
  z.string().regex(/^[a-zA-Z0-9_.]+$/, "Geçersiz değişken adı"),
  z.string().max(500, "Değişken değeri en fazla 500 karakter olabilir"),
);

export const CONTRACT_TEMPLATE_VARIABLE_ERRORS = {
  missing: "Şablondaki bazı değişkenler doldurulmadı",
  unknown: "Şablonda olmayan bir değişkene değer verildi",
} as const;
